/**
 * Client-side report export for the agents + migrations report views. Reuses the
 * multi-agent exporter (react-markdown + remark-gfm + PRINT_CSS + browser
 * Save-as-PDF, or a real .docx / .md blob) so the studio downloads are styled the
 * SAME way the user already gets in multi-agent — instead of the backend's raw
 * fpdf2 write_html output (which we no longer emit).
 *
 * IMPORTANT: for PDF the print window MUST be opened synchronously inside the
 * click handler (before any await) so the click's user-activation is still live —
 * otherwise the browser backgrounds the popup and window.print() emits only the
 * first page. downloadMarkdownAs takes that pre-opened window as `printWin`.
 */
import toast from "react-hot-toast";
import { downloadMarkdownAs, type ExportFormat } from "@/services/multi-agent/exportArtifact";

export function downloadReportAs(markdown: string, format: ExportFormat, title: string, base?: string): void {
  const md = (markdown || "").trim();
  if (!md) {
    toast.error("No report to download.");
    return;
  }
  // Open the print window SYNCHRONOUSLY in the click handler (PDF only) — see note above.
  const printWin = format === "pdf" ? window.open("", "_blank") : null;
  void downloadMarkdownAs(md, format, base || title || "report", {
    title: (title || "Report").slice(0, 90),
    printWin,
  }).catch((e) => {
    try { printWin?.close(); } catch { /* ignore */ }
    toast.error(`Download failed: ${(e as Error)?.message ?? String(e)}`);
  });
}

// Back-compat thin wrapper.
export function downloadReportPdf(markdown: string, title: string, base?: string): void {
  downloadReportAs(markdown, "pdf", title, base);
}
